import { TFunction } from "i18next";
import { isNumericGuard } from "utils/dataManipulation";
import { valueOrAlternative } from "utils/namesManipulation";

import { convertSecondsToMinutesAndSeconds } from "./date";

export const formatDurationToMinutesAndSeconds = (durationSeconds: null | number | undefined): string => {
  const altValue = valueOrAlternative(undefined) as string;
  if (!isNumericGuard(durationSeconds)) return altValue;

  const result = convertSecondsToMinutesAndSeconds(durationSeconds!);
  if (!result) return altValue;

  const { minutes, seconds } = result;
  if (!minutes && !seconds) return altValue;

  if (!minutes) return `${seconds}s`;

  if (!seconds) return `${minutes}m`;

  return `${minutes}m ${seconds}s`;
};

export const formatDateToTimeString = (date: Date | null | undefined): string | undefined => {
  if (!date) return;
  const hour = formatTo2DigitString(date.getHours());
  const minute = formatTo2DigitString(date.getMinutes());
  const second = formatTo2DigitString(date.getSeconds());

  return `${hour}:${minute}:${second}`;
};

export const formatTo2DigitString = (value: number | undefined): string => {
  if (!isNumericGuard(value)) return "";
  return value.toString().padStart(2, "0");
};

export const toStringValue = (value?: null | number | string | undefined) =>
  value == null || value === undefined || value === "" ? "" : String(value);

export const toNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const toNumberWithFallback = (value: string, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const toNumberFromUnknown = (value: unknown) => {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") return Number(value);
  return 0;
};

export const toOptionalString = (value: string) => (value.trim() === "" ? undefined : value);

export const hourToTimeValue = (hour?: null | number) => {
  if (hour == null || Number.isNaN(hour)) return "";
  const wholeHour = Math.floor(hour);
  return `${String(wholeHour).padStart(2, "0")}:00`;
};

export const timeValueToHour = (value: string) => {
  if (!value) return undefined;
  const [hours] = value.split(":");
  const parsed = Number(hours);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const formatBoolean = (t: TFunction, value?: boolean) =>
  value == null ? valueOrAlternative<string>("") : value ? t("general.yes") : t("general.no");
